import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { User } from 'types/User';
import { UserPermission } from 'constants/userPermission';
import {
  canSetStatusManually,
  canSetStatusManuallyLegacy,
} from 'utils/statuses';
import { isPostWorkflowApiNew } from 'app/config/postWorkflowApiMode';

/**
 * Where a manual status change is offered. Only used to keep non-allowlisted accounts on their
 * current behaviour, which differs between the two surfaces.
 *
 * [POST-WORKFLOW GATE — TEMPORARY] Delete along with the gate after full cutover.
 */
export type ManualStatusChangeSurface = 'postDetail' | 'timeSavers';

/** Not offered in the Time Savers bulk status change; the post-detail dropdown still offers them. */
const STATUSES_HIDDEN_FROM_BULK_CHANGE = [
  STATUS.SCHEDULED,
  STATUS.EXPORTED,
] as number[];

/**
 * Narrows `availableStatuses` to the statuses the user may set manually.
 *
 * Allowlisted accounts get the unified rule: a status that publishes the post requires
 * POST_SCHEDULE, the same permission that gates every other scheduling control. Both surfaces
 * therefore offer an identical list.
 *
 * [POST-WORKFLOW GATE — TEMPORARY] Non-allowlisted accounts keep today's behaviour exactly, which
 * is asymmetric: the post-detail dropdown applies the old role-based filter, while Time Savers
 * applies no filter at all. After full cutover, drop the gate, the `role`/`surface` parameters and
 * `canSetStatusManuallyLegacy`.
 */
export const filterManuallySettableStatuses = (
  availableStatuses: number[],
  permissionsKeys: UserPermission[],
  role: User['role'],
  surface: ManualStatusChangeSurface,
): number[] => {
  if (!isPostWorkflowApiNew()) {
    return surface === 'timeSavers'
      ? availableStatuses
      : availableStatuses.filter((status) =>
          canSetStatusManuallyLegacy(role, status),
        );
  }

  const settable = availableStatuses.filter((status) =>
    canSetStatusManually(status, permissionsKeys),
  );

  if (surface === 'timeSavers') {
    return settable.filter(
      (status) => !STATUSES_HIDDEN_FROM_BULK_CHANGE.includes(status),
    );
  }

  return settable;
};
